import { useState, useCallback } from 'react';
import { message } from 'antd';
import { p251Api, FP610, FP611 } from '../../../services/p251Api';
import { useAuth } from '../../../contexts/AuthContext';
import { Mode } from './useP251Mode';
import { recalcDetail, recalcTotals } from '../helpers';

export function useP251Record() {
  const { user } = useAuth();
  const [record, setRecord] = useState<FP610 | null>(null);
  const [formData, setFormData] = useState<Partial<FP610>>({});
  const [details, setDetails] = useState<Partial<FP611>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<FP610 | null> => {
    setLoading(true);
    try {
      const data = await p251Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details || []);
      setSelectedRow(0);
      return data;
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FP610, value: FP610[keyof FP610]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FP610): FP610[keyof FP610] => {
    return (formData as Record<string, FP610[keyof FP610]>)[key] ?? '';
  }, [formData]);

  /** Get a detail field value from the selected row */
  const getDetailField = useCallback((key: keyof FP611): FP611[keyof FP611] => {
    const sel = details[selectedRow] as Partial<FP611> | undefined;
    return sel ? (sel as Record<string, FP611[keyof FP611]>)[key] ?? '' : '';
  }, [details, selectedRow]);

  /** Set a detail field value on the selected row */
  const setDetailField = useCallback((key: keyof FP611, value: FP611[keyof FP611]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], [key]: value };
      }
      return next;
    });
  }, [selectedRow]);

  /** Update a detail row and recalculate computed fields (BR-C01) */
  const setDetailFieldWithCalc = useCallback((rowIdx: number, key: keyof FP611, value: FP611[keyof FP611]) => {
    setDetails(prev => {
      const next = [...prev];
      if (!next[rowIdx]) return prev;
      next[rowIdx] = { ...next[rowIdx], [key]: value };
      // Recalculate row computed fields
      next[rowIdx] = recalcDetail(next[rowIdx], formData);
      return next;
    });
  }, [formData]);

  /** Recalculate all details and master totals */
  const recalcAll = useCallback(() => {
    setDetails(prev => {
      const updated = prev.map(d => recalcDetail(d, formData));
      // Update master totals (BR-C02)
      const totals = recalcTotals(updated);
      setFormData(f => ({ ...f, ...totals }));
      return updated;
    });
  }, [formData]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FP610) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FP610,
        details: details as FP611[],
      };
      if (mode === 'add') {
        const result = await p251Api.create(req);
        message.success(`新增成功: ${result.fp610_01}`);
        onSuccess(result);
      } else {
        const result = await p251Api.update(formData.fp610_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        const summary = errors.slice(0, 5).map(e => e.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await p251Api.delete(record.fp610_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a new detail row (BR-F16) */
  const addDetailRow = useCallback(() => {
    const currentCount = details.length;
    if (currentCount >= 999) {
      message.warning('最多 999 筆明細');
      return;
    }
    // Copy COLOR and 毛重變量 from last row
    const lastRow = details[details.length - 1];
    const newRow: Partial<FP611> = {
      fp611_02: currentCount + 1,
      fp611_01: formData.fp610_01 || '',
      fp611_21: 0,
      fp611_22: 0,
      fp611_23: 0,
      fp611_24: 0,
      fp611_12: lastRow?.fp611_12 || '',
      fp611_442: lastRow?.fp611_442 || 0,
    };
    setDetails(prev => [...prev, newRow]);
    setFormData(prev => ({ ...prev, fp610_16: currentCount + 1 }));
  }, [details, formData.fp610_01]);

  /** Delete the currently selected detail row (BR-F17) */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 1) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedRow);
      // Re-index 1-based (T-F05)
      return next.map((d, i) => ({ ...d, fp611_02: i + 1 }));
    });
    setSelectedRow(Math.max(0, selectedRow - 1));
    setFormData(prev => ({ ...prev, fp610_16: details.length - 1 }));
  }, [details.length, selectedRow]);

  /** Insert a detail row at current position (BR-F18) */
  const insertDetailRow = useCallback(() => {
    if (details.length >= 999) {
      message.warning('最多 999 筆明細');
      return;
    }
    const newRow: Partial<FP611> = {
      fp611_02: selectedRow + 1,
      fp611_01: formData.fp610_01 || '',
      fp611_21: 0,
      fp611_22: 0,
      fp611_23: 0,
      fp611_24: 0,
    };
    setDetails(prev => {
      const next = [...prev];
      next.splice(selectedRow, 0, newRow);
      // Re-index
      return next.map((d, i) => ({ ...d, fp611_02: i + 1 }));
    });
    setFormData(prev => ({ ...prev, fp610_16: details.length + 1 }));
  }, [details, selectedRow, formData.fp610_01]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record]);

  /** Reset form for a new add (BR-F06/BR-D01) */
  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({
      fp610_11: new Date().toISOString().substring(0, 10) + 'T00:00:00Z',
      fp610_22: 0.9144,
      fp610_15: 'CM*CM*CM(YDS)',
      fp610_91: user?.userId || '',
      fp610_21: 1,
      fp610_26: 1,
      fp610_27: 1,
      fp610_28: 0,
      fp610_16: 0,
      fp610_17: 0,
      fp610_18: 0,
      fp610_19: 0,
      fp610_20: 0,
    });
    setDetails([]);
    setSelectedRow(0);
  }, [user]);

  /** Apply result after save */
  const applyResult = useCallback((result: FP610) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  return {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    setDetailFieldWithCalc, recalcAll,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow, insertDetailRow,
    revertToRecord, resetForAdd, applyResult,
  };
}
